import React, { type ReactNode, useRef } from 'react';

import { Portal, PortalArea } from '@pkg/components';
import { type OutsideClickExclusion, useKeyPress, useOutsideClick } from '@pkg/hooks';
import { noop } from '@pkg/utils';

import { useLockBodyScroll } from '@app/helpers';

import { Footer, Header, Main } from './sections';

import { Backdrop, Interior, StyledDrawer } from './Drawer.style';

export interface DrawerProps {
    children: ReactNode;
    backdrop?: boolean;
    fullscreen?: boolean;
    closeExclusions?: OutsideClickExclusion;
    onClose?(): void;
}

// TODO: Missing the following features:
// 1. Focus trapping
// 2. Accessibility attributes
export function Drawer({ children, backdrop = false, fullscreen = false, closeExclusions, onClose }: DrawerProps) {
    const drawerRef = useRef(null);

    // TODO: Replace this with `useScrollLock()` from:
    // https://github.com/beefchimi/react-hooks/pull/47
    useLockBodyScroll(fullscreen || backdrop);

    // Worth considering removing this, since on `mobile`
    // we don't really have `Escape` key presses.
    useKeyPress(['Escape'], onClose ?? noop, {
        disabled: !onClose,
    });

    useOutsideClick(drawerRef.current, onClose ?? noop, {
        exclude: closeExclusions,
        disabled: !onClose,
    });

    const backdropMarkup = fullscreen || backdrop ? <Backdrop key="Drawer-Backdrop" /> : null;

    return (
        <Portal destination={PortalArea.Transition}>
            {backdropMarkup}

            <StyledDrawer ref={drawerRef} $fullscreen={fullscreen}>
                <Interior>{children}</Interior>
            </StyledDrawer>
        </Portal>
    );
}

Drawer.Header = Header;
Drawer.Footer = Footer;
Drawer.Main = Main;
